/**
 * PlayingScreen - Main gameplay screen
 *
 * Composes:
 * - RoomBackground (full screen background image)
 * - PlayScreenHeader (always visible when active)
 * - AnimationBackdrop (dark overlay for item/educational animations)
 * - HUD (stats display, overlay triggers — animates in when phaseReady)
 * - PostMessageAnimationLayer (toasts, item-appear, educational)
 * - Message (current message with response options)
 * - Overlays (Achievements, AccountOverviewPanel)
 * - PhaseTransitionOverlay (lottie + room zoom between phases)
 * - GameEndSequence (final screen)
 *
 * State machine:
 * - isActive becomes true → phaseReady = true, HUD animates in, timer starts
 * - queue idle + has next phase → transitioning = true, phaseReady = false
 * - overlay calls onComplete → transitioning = false, phaseReady = true
 * - queue idle + last phase → finishGame()
 * - gameIsFinished → GameEndSequence
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { RoomBackground } from "@/components/screens/PlayingScreen/components/RoomBackground";
import HUD from "@/components/screens/PlayingScreen/components/Hud/Hud";
import { Message } from "@/components/screens/PlayingScreen/components/Message/Message";
import { Achievements } from "@/components/screens/PlayingScreen/components/AchievementsPanel/Achievements";
import { AccountOverviewPanel } from "@/components/screens/PlayingScreen/components/AccountOverviewPanel/AccountOverviewPanel";
import { useGameStore } from "@/state/gameStore";
import { usePhases } from "@/state/cacheStoreHooks";
import PlayScreenHeader from "@/components/screens/PlayingScreen/components/PlayScreenHeader";
import { PostMessageAnimationLayer } from "@/components/screens/PlayingScreen/components/PostMessageAnimationLayer/PostMessageAnimationLayer";
import { useMessageQueueTimer } from "@/hooks/useMessageQueueTimer";
import { PhaseTransitionOverlay } from "@/components/screens/PlayingScreen/components/PhaseTransitionOverlay";
import GameEndSequence from "@/components/screens/GameEndSequence/GameEndSequence";
import { AnimationBackdrop } from "@/components/screens/PlayingScreen/components/AnimationBackdrop";
import { useCurrentRoom } from "@/state/selectors";
import { usePreloadMessageAssets } from "@/libs/assetPreloading/usePreloadMessageAssets";
import { usePreloadPhaseTransition } from "@/libs/assetPreloading/usePreloadPhaseTransition";

type Props = {
    isActive: boolean;
    onBackgroundReady?: () => void;
};

export function PlayingScreen({ isActive, onBackgroundReady }: Props) {
    const currentRoom = useCurrentRoom();
    const roomVariant = useGameStore((s) => s.currentRoomVariant);
    const gameIsFinished = useGameStore((s) => s.gameIsFinished);
    const currentMessage = useGameStore((s) => s.currentMessage);
    const messageQueue = useGameStore((s) => s.messageQueue);
    const currentPostMessageAnimationStep = useGameStore((s) => s.currentPostMessageAnimationStep);
    const currentPhaseId = useGameStore((s) => s.currentPhaseId);
    const finishGame = useGameStore((s) => s.finishGame);
    const startMessageQueue = useGameStore((s) => s.startMessageQueue);
    const clearNextRoom = useGameStore((s) => s.clearNextRoom);

    const phases = usePhases();

    const [transitioning, setTransitioning] = useState(false);
    const [phaseReady, setPhaseReady] = useState(false);

    // Delay before HUD animates in and messages start at each phase entry
    const PHASE_READY_DELAY_MS = 300;

    // Run the queue timer
    useMessageQueueTimer();

    // Preload next phase assets when approaching a PreRoomTransition message
    const transitionPreloadStatus = usePreloadPhaseTransition();
    usePreloadMessageAssets(roomVariant);
    // --- Derived state ---
    const isIdle = currentMessage == null && messageQueue.length === 0 && currentPostMessageAnimationStep === null;

    const hasNextPhase = useMemo(() => {
        if (!currentPhaseId) return false;
        const currentIndex = phases.findIndex((p) => p.documentId === currentPhaseId);
        return currentIndex !== -1 && currentIndex < phases.length - 1;
    }, [phases, currentPhaseId]);

    // --- Initial entry: delay then phaseReady ---
    useEffect(() => {
        if (currentRoom && isActive && !transitioning && !phaseReady) {
            const id = setTimeout(() => setPhaseReady(true), PHASE_READY_DELAY_MS);
            return () => clearTimeout(id);
        }

        // If we become active but have no current room, that's because the game was restarted from the end screen
        // this for sure could be handled better in the future
        if (!currentRoom) {
            setPhaseReady(false);
        }
    }, [isActive, transitioning, phaseReady, currentRoom]);

    // --- Start the message queue when phase becomes ready ---
    useEffect(() => {
        if (phaseReady) {
            startMessageQueue();
        }
    }, [phaseReady, startMessageQueue]);

    // --- Phase transition: idle + has next phase → start transition ---
    useEffect(() => {
        if (isIdle && !gameIsFinished && hasNextPhase && !transitioning) {
            setTransitioning(true);
            setPhaseReady(false);
        }
    }, [isIdle, gameIsFinished, hasNextPhase, transitioning]);

    // --- Last phase exhausted → finish the game ---
    useEffect(() => {
        if (isIdle && !gameIsFinished && !hasNextPhase && currentPhaseId != null) {
            finishGame();
        }
    }, [isIdle, gameIsFinished, hasNextPhase, currentPhaseId, finishGame]);

    const handleTransitionComplete = useCallback(() => {
        setTransitioning(false);
        clearNextRoom();
        setTimeout(() => setPhaseReady(true), PHASE_READY_DELAY_MS);
    }, []);

    return (
        <div className="relative size-full bg-black">
            {/* Background layer — always visible */}
            <RoomBackground onReady={onBackgroundReady} />

            <AnimationBackdrop />

            {/* Header — always visible when active */}
            {isActive && <PlayScreenHeader />}

            {/* HUD — animates in/out based on phaseReady (and its own internal conditions) */}
            <HUD phaseReady={phaseReady} />

            {/* Animation layer - toasts, item appear, educational */}
            <PostMessageAnimationLayer />

            {/* Message UI layer */}
            <Message />

            {/* Overlay layer */}
            <Achievements />
            <AccountOverviewPanel />

            {/* Phase transition overlay (lottie + room zoom) */}
            {transitioning && (
                <PhaseTransitionOverlay preloadStatus={transitionPreloadStatus} onComplete={handleTransitionComplete} />
            )}

            {gameIsFinished && <GameEndSequence />}
        </div>
    );
}
